// eslint-disable-next-line import/extensions
import './boomerang.js';
import { get } from 'axios';
import { COUNTRY_CODE_INFO_API } from 'config/constants';
import TEST_CONFIG from './testConfig';

declare global {
  const BOOMR: any;
}

export type ClientTestResult = {
  id:
    | 'latency'
    | 'lab-bandwidth'
    | 'ws'
    | 'wss'
    | 'resolution'
    | 'client-timeout';
  name: string;
  status: 'success' | 'error' | 'surpassed';
  details: string;
  timestamp: string;
  duration: number; // in ms
};

export const getUserRegion = async (country: string): Promise<string> => {
  try {
    const response = await get(COUNTRY_CODE_INFO_API, {
      params: { country_code: country },
    });

    const region = response.data?.region;
    if (!region) {
      return 'northamerica'; // defaults to NA
    }
    return region;
  } catch {
    return 'northamerica'; // defaults to NA on error
  }
};

// ----------------------------------------------------------------------
// Configuration (Placeholders derived from lwccheck.js usage)
// ----------------------------------------------------------------------
const CONFIG = {
  // Bandwidth configuration from lwccheck.js runTests()
  bwAmount: 10,
  bwTfet: 3000,
  // Limits for pass/fail comparison (from lwccheck.js before_beacon)
  bw_poor_limit_total: TEST_CONFIG.bw_poor_limit,
  bw_good_limit_total: TEST_CONFIG.bw_good_limit,
  lat_poor_limit: TEST_CONFIG.lat_poor_limit,
  lat_good_limit: TEST_CONFIG.lat_good_limit,
};
// ----------------------------------------------------------------------

/**
 * Helper to determine result status and format the details string.
 */
const processResult = (
  id: 'latency' | 'lab-bandwidth' | 'ws' | 'wss' | 'resolution',
  name: string,
  passCondition: boolean,
  surpassCondition: boolean,
  measurement: number,
  error: number,
  config: typeof CONFIG,
): ClientTestResult => {
  let status: ClientTestResult['status'];
  let details: string;

  const now = new Date();

  // Logic ported from lwccheck.js before_beacon
  if (surpassCondition) {
    status = 'surpassed';
  } else if (passCondition) {
    status = 'success';
  } else {
    status = 'error';
  }

  // Generate details string based on test
  if (id === 'lab-bandwidth') {
    details = `Bandwidth: ${measurement} Kbps (Error: ${error}%) | Poor < ${config.bw_poor_limit_total}, Good > ${config.bw_good_limit_total}`;
  } else {
    details = `Latency: ${measurement} ms (Error: ${error}) | Poor > ${config.lat_poor_limit}, Good < ${config.lat_good_limit}`;
  }

  return {
    id,
    name,
    status,
    details,
    timestamp: now.toISOString(),
    duration: 0,
  };
};

/**
 * Implements the 'ws' (Web Sockets) test.
 * Checks for window.WebSocket support.
 */
const runWebSocketsTest = (): ClientTestResult => {
  const start = new Date().getTime();
  const supported = 'WebSocket' in window;
  const status = supported ? 'success' : 'error';
  const details = supported
    ? 'The browser supports WebSockets.'
    : 'The browser does not support WebSockets.';

  const end = new Date().getTime();

  return {
    id: 'ws',
    name: 'WebSockets support',
    status,
    details,
    timestamp: new Date().toISOString(),
    duration: end - start,
  };
};

/**
 * Implements the 'wss' (Secure Web Sockets) connection test.
 */
const runSecureWebSocketsTest = (): Promise<ClientTestResult> => {
  const start = new Date().getTime();
  return new Promise((resolve) => {
    // Timeout of 5 seconds
    const timeoutId = setTimeout(() => {
      resolve({
        id: 'wss',
        name: 'Secure WebSockets support',
        status: 'error',
        details: 'Secure WebSocket connection timed out.',
        timestamp: new Date().toISOString(),
        duration: new Date().getTime() - start,
      });
    }, 5000);

    let websocket: WebSocket;
    try {
      websocket = new WebSocket(TEST_CONFIG.wss_test_url);
    } catch (e) {
      clearTimeout(timeoutId);
      resolve({
        id: 'wss',
        name: 'Secure WebSockets support',
        status: 'error',
        details: 'Failed to create WebSocket object.',
        timestamp: new Date().toISOString(),
        duration: new Date().getTime() - start,
      });
      return;
    }

    websocket.onopen = () => {
      websocket.close();
      clearTimeout(timeoutId);
      resolve({
        id: 'wss',
        name: 'Secure WebSockets support',
        status: 'success',
        details: 'The browser was able to connect using WebSockets.',
        timestamp: new Date().toISOString(),
        duration: new Date().getTime() - start,
      });
    };

    // Treat any connection or runtime error as failure
    websocket.onerror = () => {
      websocket.close();
      clearTimeout(timeoutId);
      resolve({
        id: 'wss',
        name: 'Secure WebSockets support',
        status: 'error',
        details:
          'The browser was not able to connect using WebSockets (connection error).',
        timestamp: new Date().toISOString(),
        duration: new Date().getTime() - start,
      });
    };
  });
};

/**
 * Implements the 'resolution' test.
 * Checks for minimum screen resolution of 1280x800px.
 */
const runResolutionTest = (): ClientTestResult => {
  const start = new Date().getTime();
  const { xres, yres } = TEST_CONFIG;

  const currentWidth = window.screen.width;
  const currentHeight = window.screen.height;

  const passed = currentWidth >= xres && currentHeight >= yres;
  const status = passed ? 'success' : 'error';

  const details = passed
    ? `The screen fulfills the requirement of a minimum resolution of ${xres}x${yres} px. (Current: ${currentWidth}x${currentHeight})`
    : `The screen resolution does not fulfill the requirement of a minimum resolution of ${xres}x${yres} px. (Current: ${currentWidth}x${currentHeight})`;

  const end = new Date().getTime();

  return {
    id: 'resolution',
    name: 'Resolution',
    status,
    details,
    timestamp: new Date().toISOString(),
    duration: end - start,
  };
};

/**
 * Runs the Bandwidth and Latency tests using Boomerang and returns the results.
 * This replaces the core runTests and BOOMR.subscribe('before_beacon') logic.
 */
export const runNetworkTests = async (
  base_url: string,
): Promise<ClientTestResult[]> => {
  if (typeof BOOMR === 'undefined') {
    console.error(
      'BOOMR is not loaded. Ensure the Boomerang script is included.',
    );
    return [
      {
        id: 'lab-bandwidth',
        name: 'lab-bandwidth',
        status: 'error',
        details: 'BOOMR library not found.',
        timestamp: new Date().toISOString(),
        duration: 0,
      },
      {
        id: 'latency',
        name: 'latency',
        status: 'error',
        details: 'BOOMR library not found.',
        timestamp: new Date().toISOString(),
        duration: 0,
      },
    ];
  }

  const NETWORK_TEST_TIMEOUT_MS = 45 * 1000; // 45 seconds (BOOMR's own timeout is ~30s)

  return new Promise((resolve) => {
    let resolved = false;
    let bwResult: ClientTestResult | null = null;
    let latResult: ClientTestResult | null = null;
    const startTime = new Date().getTime();

    const finishWith = (results: ClientTestResult[]) => {
      if (resolved) return;
      resolved = true;
      resolve(results);
    };

    // Failsafe: if the beacon never fires, resolve with error results
    const networkTimeoutId = setTimeout(() => {
      finishWith([
        {
          id: 'lab-bandwidth',
          name: 'lab-bandwidth',
          status: 'error',
          details:
            'Bandwidth test timed out. The server may be unreachable from your network.',
          timestamp: new Date().toISOString(),
          duration: NETWORK_TEST_TIMEOUT_MS,
        },
        {
          id: 'latency',
          name: 'latency',
          status: 'error',
          details:
            'Latency test timed out. The server may be unreachable from your network.',
          timestamp: new Date().toISOString(),
          duration: NETWORK_TEST_TIMEOUT_MS,
        },
      ]);
    }, NETWORK_TEST_TIMEOUT_MS);

    // The subscription handler processes results when the beacon fires
    BOOMR.subscribe('before_beacon', (o: any) => {
      clearTimeout(networkTimeoutId);
      const stopTime = new Date().getTime();
      const duration = stopTime - startTime;

      if (o.bw) {
        // Bandwidth calculation and status logic ported from lwccheck.js
        const kbps = parseInt((o.bw * 8) / 1024, 10);
        const kbpsErr = parseInt((o.bw_err * 100) / o.bw, 10);
        const config = CONFIG;

        const passCondition = kbps >= config.bw_poor_limit_total;
        const surpassCondition = kbps >= config.bw_good_limit_total;

        bwResult = processResult(
          'lab-bandwidth',
          'lab-bandwidth',
          passCondition,
          surpassCondition,
          kbps,
          kbpsErr,
          config,
        );
        bwResult.duration = duration;
      } else {
        bwResult = {
          id: 'lab-bandwidth',
          name: 'lab-bandwidth',
          status: 'error',
          details: 'Bandwidth test failed: no data received from the server.',
          timestamp: new Date().toISOString(),
          duration,
        };
      }

      if (o.lat) {
        // Latency calculation and status logic ported from lwccheck.js
        const lat = parseInt(o.lat, 10);
        const latErr = o.lat_err;
        const config = CONFIG;

        const passCondition = lat <= config.lat_poor_limit;
        const surpassCondition = lat <= config.lat_good_limit;

        latResult = processResult(
          'latency',
          'latency',
          passCondition,
          surpassCondition,
          lat,
          latErr,
          config,
        );
        latResult.duration = duration;
      } else {
        latResult = {
          id: 'latency',
          name: 'latency',
          status: 'error',
          details: 'Latency test failed: no data received from the server.',
          timestamp: new Date().toISOString(),
          duration,
        };
      }

      // Both results are now always set (success or error)
      finishWith([bwResult, latResult]);
    });

    // Initialization logic ported from lwccheck.js runTests
    BOOMR.init({
      autorun: false,
      test_https: true,
      site_domain: 'redhat.com',
      BW: {
        test_https: true,
        block_beacon: true,
        cookie: 'BOOMR-BW',
        nruns: CONFIG.bwAmount,
        timeout: CONFIG.bwAmount * CONFIG.bwTfet,
        base_url,
      },
      RT: {
        // RT is the Real-Time (Latency) plugin
        cookie: 'BOOMR-RT',
      },
    });

    // Explicitly start the tests (similar to what the legacy code did after init/prepare)
    BOOMR.page_ready();
  });
};

/**
 * Runs client-side network tests and returns the results.
 * We no longer submit from here; the store handles the combined submission.
 */
export const executeClientTests = async (
  base_url: string,
): Promise<ClientTestResult[]> => {
  try {
    // Wait for the tests to complete
    const networkTestResults = await runNetworkTests(base_url);
    const webSocketsTestResults = await runWebSocketsTest();
    const secureWebSocketsTestResults = await runSecureWebSocketsTest();
    const resolutionTestResults = await runResolutionTest();

    const results = networkTestResults.concat(
      webSocketsTestResults,
      secureWebSocketsTestResults,
      resolutionTestResults,
    );

    // Normalize status 'surpassed' to 'success' before returning
    return results.map((r) => ({
      ...r,
      status: r.status === 'surpassed' ? 'success' : r.status,
    }));
  } catch (error) {
    console.error('Error in executeClientTests:', error); // eslint-disable-line no-console
    return [];
  }
};
